import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  FileText, 
  Plus, 
  MessageSquare, 
  Download, 
  Filter,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Benvenuto nel Portale Clienti',
    description: 'Questo tutorial ti guiderà attraverso le funzionalità principali del portale per la gestione delle segnalazioni.',
    icon: HelpCircle,
    highlight: null
  },
  {
    id: 'dashboard',
    title: 'La tua Dashboard',
    description: 'Qui trovi tutte le tue segnalazioni organizzate in tab: Assegnate a te, Create da te, Monitorate e Tutte del progetto.',
    icon: FileText,
    highlight: '[data-testid="customer-tabs"]'
  },
  {
    id: 'create-issue',
    title: 'Creare una Segnalazione',
    description: 'Clicca sul pulsante "Nuova Segnalazione" per aprire il wizard guidato che ti aiuterà a compilare tutti i campi necessari.',
    icon: Plus,
    highlight: '[data-testid="new-issue-button"]'
  },
  {
    id: 'filters',
    title: 'Filtrare le Segnalazioni',
    description: 'Usa i filtri per progetto, stato e data per trovare rapidamente le segnalazioni che cerchi.',
    icon: Filter,
    highlight: '[data-testid="filters-section"]'
  },
  {
    id: 'issue-detail',
    title: 'Dettaglio Segnalazione',
    description: 'Clicca su una segnalazione per vedere tutti i dettagli, le note e gli allegati. Puoi anche aggiungere commenti.',
    icon: MessageSquare,
    highlight: null
  },
  {
    id: 'attachments',
    title: 'Gestione Allegati',
    description: 'Puoi allegare file alle tue segnalazioni e scaricare quelli esistenti cliccando sull\'icona di download.',
    icon: Download,
    highlight: null
  },
  {
    id: 'complete',
    title: 'Tutto Pronto!',
    description: 'Hai completato il tutorial. Ora sei pronto per utilizzare il portale. Buon lavoro!',
    icon: CheckCircle2,
    highlight: null
  }
];

function CustomerTutorial({ onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  useEffect(() => {
    // Highlight element if specified
    if (step.highlight) {
      const element = document.querySelector(step.highlight);
      if (element) {
        element.classList.add('tutorial-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    return () => {
      // Remove highlight from all elements
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, [currentStep, step.highlight]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    setIsVisible(false);
    if (onSkip) onSkip();
  };

  if (!isVisible) return null;

  const IconComponent = step.icon;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={handleSkip} />
      
      {/* Tutorial Card */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg">
        <div className="bg-[rgb(24,24,27)] border border-[rgb(63,63,70)] rounded-xl shadow-2xl overflow-hidden">
          {/* Progress Bar */}
          <div className="h-1 bg-[rgb(39,39,42)]">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[rgb(39,39,42)]">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-[rgb(161,161,170)]">
                Passo {currentStep + 1} di {TUTORIAL_STEPS.length}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSkip}
              className="text-[rgb(161,161,170)] hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/10 flex items-center justify-center">
              <IconComponent className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-3">
              {step.title}
            </h2>
            <p className="text-[rgb(161,161,170)] font-mono text-sm leading-relaxed">
              {step.description}
            </p>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-[rgb(39,39,42)] bg-[rgb(18,18,20)]">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-[rgb(161,161,170)] font-mono text-sm"
            >
              Salta Tutorial
            </Button>
            <div className="flex gap-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  className="font-mono"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="bg-blue-600 hover:bg-blue-700 font-mono"
              >
                {isLastStep ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Inizia
                  </>
                ) : (
                  <>
                    Avanti
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS for highlighting */}
      <style>{`
        .tutorial-highlight {
          position: relative;
          z-index: 51;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3);
          border-radius: 8px;
          animation: pulse-highlight 2s infinite;
        }
        
        @keyframes pulse-highlight {
          0%, 100% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.3), 0 0 30px rgba(59, 130, 246, 0.2); }
        }
      `}</style>
    </>
  );
}

export default CustomerTutorial;
